import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { thumbStyle } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Heart, Search, Plus, LogOut, Sparkles, Shield, CheckCircle, ChevronLeft, ChevronRight, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import Pagination from '@/components/Pagination';
import { formatPetDate } from '@/utils/formatDate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAGE_SIZE = 24;

const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  const [myPosts, setMyPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchMyPosts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: String(page * PAGE_SIZE),
        limit: String(PAGE_SIZE),
      });
      const response = await axios.get(`${API}/pets/my-posts?${params.toString()}`, {
        withCredentials: true,
      });
      // El backend ya devuelve match_count por publicación (sin N+1).
      setMyPosts(response.data?.items || []);
      setTotal(response.data?.total || 0);
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast.error('Error al cargar tus publicaciones');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchMyPosts();
  }, [fetchMyPosts]);

  const maxPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      toast.success('Sesión cerrada');
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Error al cerrar sesión');
    }
  };

  const deletePost = async (petId) => {
    try {
      await axios.delete(`${API}/pets/${petId}`, { withCredentials: true });
      toast.success('Publicación eliminada');
      fetchMyPosts();
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar la publicación');
    }
  };

  const findMatches = async (petId) => {
    try {
      toast.loading('Buscando coincidencias...');
      await axios.post(`${API}/matches/find/${petId}`, { force_refresh: true }, {
        withCredentials: true,
      });
      toast.dismiss();
      navigate(`/pet/${petId}`);
    } catch (error) {
      toast.dismiss();
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      if (status === 429) {
        // Límite diario del dueño alcanzado: igual lo llevamos a la ficha a ver el estado actual.
        toast.info(detail || 'Solo podés actualizar las coincidencias una vez por día.');
        navigate(`/pet/${petId}`);
      } else {
        console.error('Error finding matches:', error);
        toast.error(detail || 'Error al buscar coincidencias');
      }
    }
  };

  return (
    <div className="min-h-screen gradient-mesh">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-border sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Heart className="h-6 w-6 text-primary" fill="currentColor" />
            <h1 className="text-xl font-fraunces font-semibold text-foreground">MiAmigoFiel</h1>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground hidden md:inline">
              {user?.name}
            </span>
            {user?.role === 'admin' && (
              <Button
                variant="outline"
                size="sm"
                className="rounded-full gap-1"
                onClick={() => navigate('/admin')}
              >
                <Shield className="h-4 w-4" /> Admin
              </Button>
            )}
            <Button
              data-testid="logout-button"
              variant="ghost"
              onClick={handleLogout}
              className="rounded-full"
              size="sm"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Quick Actions */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <Card
            data-testid="post-lost-card"
            onClick={() => navigate('/post-pet?type=lost')}
            className="card-hover cursor-pointer rounded-3xl border-border"
          >
            <CardHeader>
              <div className="bg-primary/10 rounded-2xl w-12 h-12 flex items-center justify-center mb-2">
                <Heart className="h-6 w-6 text-primary" />
              </div>
              <CardTitle className="font-fraunces">Publicar Perdida</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Reporta una mascota que perdiste
              </p>
            </CardContent>
          </Card>

          <Card
            data-testid="post-found-card"
            onClick={() => navigate('/post-pet?type=found')}
            className="card-hover cursor-pointer rounded-3xl border-border"
          >
            <CardHeader>
              <div className="bg-secondary/20 rounded-2xl w-12 h-12 flex items-center justify-center mb-2">
                <Plus className="h-6 w-6 text-secondary-foreground" />
              </div>
              <CardTitle className="font-fraunces">Publicar Encontrada</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Reporta una mascota que encontraste
              </p>
            </CardContent>
          </Card>

          <Card
            data-testid="browse-card"
            onClick={() => navigate('/lost-pets')}
            className="card-hover cursor-pointer rounded-3xl border-border"
          >
            <CardHeader>
              <div className="bg-accent/20 rounded-2xl w-12 h-12 flex items-center justify-center mb-2">
                <Search className="h-6 w-6 text-accent" />
              </div>
              <CardTitle className="font-fraunces">Buscar Mascotas</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Navega mascotas perdidas y encontradas
              </p>
            </CardContent>
          </Card>
        </div>

        {/* My Posts */}
        <div data-testid="my-posts-section">
          <h2 className="text-3xl font-fraunces font-semibold mb-6 text-foreground">
            Mis Publicaciones
          </h2>
          
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            </div>
          ) : myPosts.length === 0 ? (
            <Card className="rounded-3xl border-border">
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground mb-4">
                  Aún no tienes publicaciones
                </p>
                <Button
                  data-testid="create-first-post-button"
                  onClick={() => navigate('/post-pet')}
                  className="rounded-full btn-primary bg-primary text-primary-foreground"
                >
                  Crear Primera Publicación
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {myPosts.map((pet) => (
                  <Card
                    key={pet.pet_id}
                    data-testid={`pet-card-${pet.pet_id}`}
                    className={`card-hover cursor-pointer rounded-3xl border-border overflow-hidden ${pet.status === 'resolved' ? 'opacity-60' : ''}`}
                    onClick={() => navigate(`/pet/${pet.pet_id}`)}
                  >
                    {pet.photos && pet.photos.length > 0 && (
                      <div className="h-48 overflow-hidden">
                        <img
                          src={pet.photos[0]}
                          alt={pet.name || 'Pet'}
                          className="w-full h-full object-cover"
                          style={thumbStyle(pet.thumbnail_position, pet.thumbnail_zoom)}
                        />
                      </div>
                    )}
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <CardTitle className="font-fraunces text-lg">
                          {pet.name || 'Sin nombre'}
                        </CardTitle>
                        <div className="flex gap-1">
                          <Badge
                            variant={pet.type === 'lost' ? 'destructive' : 'default'}
                            className="rounded-full"
                          >
                            {pet.type === 'lost' ? 'Perdida' : 'Encontrada'}
                          </Badge>
                          {pet.status === 'resolved' && (
                            <Badge className="rounded-full bg-green-100 text-green-700 border-green-300">
                              Reunida
                            </Badge>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground mb-2">
                        {pet.breed || 'Raza desconocida'} • {pet.location}
                      </p>
                      <p className="text-xs text-muted-foreground mb-4">
                        {formatPetDate(pet.date_lost_found)}
                      </p>
                      {pet.status === 'resolved' ? (
                        <div className="flex items-center gap-1 text-green-600 text-sm font-medium">
                          <CheckCircle className="h-4 w-4" />
                          Caso resuelto
                        </div>
                      ) : pet.match_count > 0 ? (
                        <Badge variant="secondary" className="rounded-full">
                          <Sparkles className="h-3 w-3 mr-1" />
                          {pet.match_count} coincidencia(s)
                        </Badge>
                      ) : (
                        <Button
                          data-testid={`find-matches-${pet.pet_id}`}
                          size="sm"
                          variant="outline"
                          className="rounded-full w-full"
                          onClick={(e) => {
                            e.stopPropagation();
                            findMatches(pet.pet_id);
                          }}
                        >
                          Buscar Coincidencias
                        </Button>
                      )}

                      <div className="flex gap-2 mt-3 pt-3 border-t border-border">
                        <Button
                          data-testid={`edit-pet-${pet.pet_id}`}
                          size="sm"
                          variant="outline"
                          className="rounded-full flex-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/edit-pet/${pet.pet_id}`);
                          }}
                        >
                          <Pencil className="h-3.5 w-3.5 mr-1" /> Editar
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              data-testid={`delete-pet-${pet.pet_id}`}
                              size="sm"
                              variant="outline"
                              className="rounded-full flex-1 text-destructive hover:text-destructive"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Trash2 className="h-3.5 w-3.5 mr-1" /> Eliminar
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Eliminar publicación</AlertDialogTitle>
                              <AlertDialogDescription>
                                La publicación dejará de ser visible y no aparecerá en búsquedas ni
                                coincidencias. Podés pedirle a un administrador que la restaure.
                                ¿Querés eliminarla?
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel onClick={(e) => e.stopPropagation()}>Cancelar</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={(e) => { e.stopPropagation(); deletePost(pet.pet_id); }}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Eliminar
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {total > PAGE_SIZE && (
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-2 mt-8">
                  <p className="text-sm text-muted-foreground">
                    {total} publicación(es) — Página {page + 1} de {maxPage + 1}
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-full"
                      disabled={page === 0}
                      onClick={() => setPage(p => Math.max(0, p - 1))}
                    >
                      <ChevronLeft className="h-4 w-4" /> Anterior
                    </Button>
                    <Pagination page={page} totalPages={maxPage + 1} onPageChange={setPage} />
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-full"
                      disabled={page >= maxPage}
                      onClick={() => setPage(p => p + 1)}
                    >
                      Siguiente <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;